import { selectPair } from 'ducks/pair/selectors';
import { PAIR_OPTION } from 'enums';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { CRYPTO_PATH, SELECT_SLIM_HEIGHT, THEME } from '../../../../constants';

const { PUBLIC_URL } = process.env;

import { useCallback } from 'react';
import { useLocation, useParams } from 'react-router';
import { CURRENCY } from '@voladeum/client';
import classNames from 'classnames';
import { useClientContext } from 'client/context';
import { CandleData } from 'client/voladeum/candles/types';
import { FORMAT_TYPE } from 'cyprus/enums';
import { formatValue } from 'cyprus/formats';
import { updateModal } from 'ducks/modals/slices';
import { selectSettings } from 'ducks/settings/selectors';
import { Dropdown, DropdownCalendar } from 'framework/dropdown';
import { useAppNavigate } from 'hooks/useAppNavigate';
import { useConfirmModal } from 'hooks/useConfirmModal';
import moment from 'moment';
import { parseQuery } from 'utils/queries';

import { Card } from 'components/cards/Card';

import { pair_options } from '../constants';
import { PairOption } from '../types';

import cn from './PairInformation.module.scss';

export const PairInformation = () => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { search } = useLocation();
  const { since_milliseconds } = parseQuery(search);
  const { pair_id } = useParams();
  const { current, previous } = useAppSelector(selectSettings);

  const voladeum_pair = useAppSelector(selectPair);
  const confirmModal = useConfirmModal();
  const { Voladeum } = useClientContext();

  const onSelectCalendar = useCallback(
    ([date]: string[]) => {
      const since_milliseconds = moment.utc(date).valueOf();
      const to_milliseconds = moment.utc(date).add(1, 'day').valueOf();
      navigate.update({ since_milliseconds, to_milliseconds });
    },
    [navigate]
  );

  const onSelect = useCallback(
    async (option: PairOption) => {
      switch (option.id) {
        case PAIR_OPTION.RUN_PAIR:
          confirmModal({
            title: option.name,
            message: option.message,
            onConfirm: () => Voladeum.pairs.run(pair_id),
          });
          break;
        case PAIR_OPTION.PAUSE_PAIR:
          confirmModal({
            title: option.name,
            message: option.message,
            onConfirm: () => Voladeum.pairs.pause(pair_id),
          });
          break;
        case PAIR_OPTION.INITIALIZE_CANDLE_WORKER:
          dispatch(updateModal({ key: 'candle_workers', data: { is_open: true } }));
          break;
        case PAIR_OPTION.PAUSE_CANDLE_WORKER:
          confirmModal({
            title: option.name,
            message: option.message,
            onConfirm: () => Voladeum.pairs.pauseCandleWorkers(pair_id),
          });
          break;
        case PAIR_OPTION.RUN_CANDLE_WORKER:
          confirmModal({
            title: option.name,
            message: option.message,
            onConfirm: () => Voladeum.pairs.runCandleWorkers(pair_id),
          });
          break;
        case PAIR_OPTION.DELETE_CANDLE_WORKER:
          confirmModal({
            title: option.name,
            message: option.message,
            onConfirm: () => Voladeum.pairs.deleteCandleWorkers(pair_id),
          });
          break;
        case PAIR_OPTION.RESET_VALIDATION:
          dispatch(updateModal({ key: 'candle_validations', data: { is_open: true } }));
      }
    },
    [confirmModal, dispatch, Voladeum.pairs, pair_id]
  );

  const isPositive = useCallback(
    (key: keyof CandleData) => {
      if (current && previous) {
        return current[key] > previous[key];
      }
      return false;
    },
    [current, previous]
  );

  const isNegative = useCallback(
    (key: keyof CandleData) => {
      if (current && previous) {
        return current[key] < previous[key];
      }
      return false;
    },
    [current, previous]
  );

  const { pair_name, adapter_name, base_currency_name, base_currency_id } = voladeum_pair;
  const currency = CRYPTO_PATH[base_currency_id as CURRENCY];
  const today_milliseconds = moment.utc().valueOf();
  const start_date = formatValue(since_milliseconds || today_milliseconds, {
    format: FORMAT_TYPE.UNIX_SHORT,
    utc: true,
  });

  return (
    <Card maxHeight="fit-content">
      <div className={cn.card}>
        <div className={cn.crypto}>
          <img
            className={cn.img}
            src={`${PUBLIC_URL}/cryptocurrencies/${currency}.svg`}
            alt={base_currency_name}
          />
          <div className={cn.section}>
            <p>{pair_name}</p>
            <p className={cn.small}>{adapter_name}</p>
          </div>
        </div>
        <div className={cn.section}>
          <p>
            {formatValue(current?.x, { format: FORMAT_TYPE.UNIX_TIME, invalid: '-', utc: true })}
          </p>
          <p className={cn.small}>Time</p>
        </div>
        <div className={cn.section}>
          <p className={classNames({ [cn.green]: isPositive('o'), [cn.red]: isNegative('o') })}>
            {formatValue(current?.o, { format: FORMAT_TYPE.CURRENCY, invalid: '-' })}
          </p>
          <p className={cn.small}>Open</p>
        </div>
        <div className={cn.section}>
          <p className={classNames({ [cn.green]: isPositive('h'), [cn.red]: isNegative('h') })}>
            {formatValue(current?.h, { format: FORMAT_TYPE.CURRENCY, invalid: '-' })}
          </p>
          <p className={cn.small}>High</p>
        </div>
        <div className={cn.section}>
          <p className={classNames({ [cn.green]: isPositive('l'), [cn.red]: isNegative('l') })}>
            {formatValue(current?.l, { format: FORMAT_TYPE.CURRENCY, invalid: '-' })}
          </p>
          <p className={cn.small}>Low</p>
        </div>
        <div className={cn.section}>
          <p className={classNames({ [cn.green]: isPositive('c'), [cn.red]: isNegative('c') })}>
            {formatValue(current?.c, { format: FORMAT_TYPE.CURRENCY, invalid: '-' })}
          </p>
          <p className={cn.small}>Close</p>
        </div>
        <div className={cn.section}>
          <p className={classNames({ [cn.green]: isPositive('v'), [cn.red]: isNegative('v') })}>
            {formatValue(current?.v, { format: FORMAT_TYPE.NUMBER, decimal: 5, invalid: '-' })}
          </p>
          <p className={cn.small}>Volume</p>
        </div>
        <div className={cn.flex} />
        <DropdownCalendar
          value={[start_date]}
          placeholder="Candle Date"
          onSelect={onSelectCalendar}
          height={SELECT_SLIM_HEIGHT}
          theme={THEME.select.slim}
          extend
          autosize
        />
        <Dropdown
          value="Actions"
          options={pair_options}
          onSelect={onSelect}
          option_key="name"
          height={SELECT_SLIM_HEIGHT}
          theme={THEME.select.slim}
          right_icon={null}
          extend
          autosize
        />
      </div>
    </Card>
  );
};
